import { NetworkAvailableFeature } from '../../auth/networkFeatures';
import type { DashboardMessageKey } from '../../../lib/messages';
import type { ReportExplorerFilter, ReportFilter } from './model';
import {
  ReportDimension,
  ReportFilterField,
  ReportFilterLookupMode,
  ReportFilterMode,
} from './reportEnums';

type ReportFilterArrayKey =
  | 'agencyIds'
  | 'publisherIds'
  | 'demandPartnerIds'
  | 'dpIds'
  | 'bundleIds'
  | 'placementIds'
  | 'countries'
  | 'demandIntegrationIds'
  | 'platformIds'
  | 'lineItemIds'
  | 'appIds'
  | 'domains'
  | 'excludePublisherIds'
  | 'excludeDemandPartnerIds'
  | 'excludeDpIds'
  | 'excludePlacementIds'
  | 'excludeDemandIntegrationIds'
  | 'excludeAppIds'
  | 'excludeDomains';

export interface AvailableReportFilterDefinition {
  field: ReportFilterField;
  labelKey: DashboardMessageKey;
  includeKey: ReportFilterArrayKey;
  excludeKey?: ReportFilterArrayKey;
  lookupMode: ReportFilterLookupMode;
  requiredFeature?: NetworkAvailableFeature;
  numericValues?: boolean;
}

export const reportFilterDefinitions: Record<ReportFilterField, AvailableReportFilterDefinition> = {
  [ReportFilterField.Publisher]: {
    field: ReportFilterField.Publisher,
    labelKey: 'reportExplorer.dimension.PUBLISHER',
    includeKey: 'publisherIds',
    excludeKey: 'excludePublisherIds',
    lookupMode: ReportFilterLookupMode.Server,
  },
  [ReportFilterField.DemandPartner]: {
    field: ReportFilterField.DemandPartner,
    labelKey: 'reportExplorer.dimension.DEMAND_PARTNER',
    includeKey: 'demandPartnerIds',
    excludeKey: 'excludeDemandPartnerIds',
    lookupMode: ReportFilterLookupMode.Server,
  },
  [ReportFilterField.BidderCode]: {
    field: ReportFilterField.BidderCode,
    labelKey: 'reportExplorer.dimension.BIDDER_CODE',
    includeKey: 'dpIds',
    excludeKey: 'excludeDpIds',
    lookupMode: ReportFilterLookupMode.Local,
  },
  [ReportFilterField.BundleId]: {
    field: ReportFilterField.BundleId,
    labelKey: 'reportExplorer.dimension.APP',
    includeKey: 'bundleIds',
    lookupMode: ReportFilterLookupMode.Server,
  },
  [ReportFilterField.Placement]: {
    field: ReportFilterField.Placement,
    labelKey: 'reportExplorer.dimension.PLACEMENT',
    includeKey: 'placementIds',
    excludeKey: 'excludePlacementIds',
    lookupMode: ReportFilterLookupMode.Server,
  },
  [ReportFilterField.Country]: {
    field: ReportFilterField.Country,
    labelKey: 'reportExplorer.dimension.COUNTRY',
    includeKey: 'countries',
    lookupMode: ReportFilterLookupMode.Server,
  },
  [ReportFilterField.DemandIntegration]: {
    field: ReportFilterField.DemandIntegration,
    labelKey: 'reportExplorer.dimension.DEMAND_INTEGRATION',
    includeKey: 'demandIntegrationIds',
    excludeKey: 'excludeDemandIntegrationIds',
    lookupMode: ReportFilterLookupMode.Server,
  },
  [ReportFilterField.Platform]: {
    field: ReportFilterField.Platform,
    labelKey: 'reportExplorer.dimension.PLATFORM',
    includeKey: 'platformIds',
    lookupMode: ReportFilterLookupMode.Local,
  },
  [ReportFilterField.LineItem]: {
    field: ReportFilterField.LineItem,
    labelKey: 'reportExplorer.dimension.LINE_ITEM',
    includeKey: 'lineItemIds',
    lookupMode: ReportFilterLookupMode.Server,
    requiredFeature: NetworkAvailableFeature.Campaign,
    numericValues: true,
  },
  [ReportFilterField.Application]: {
    field: ReportFilterField.Application,
    labelKey: 'reportExplorer.dimension.APPLICATION',
    includeKey: 'appIds',
    excludeKey: 'excludeAppIds',
    lookupMode: ReportFilterLookupMode.Server,
  },
  [ReportFilterField.Domain]: {
    field: ReportFilterField.Domain,
    labelKey: 'reportExplorer.dimension.PUBLISHER_DOMAIN',
    includeKey: 'domains',
    excludeKey: 'excludeDomains',
    lookupMode: ReportFilterLookupMode.Server,
  },
  [ReportFilterField.Agency]: {
    field: ReportFilterField.Agency,
    labelKey: 'reportExplorer.dimension.AGENCY',
    includeKey: 'agencyIds',
    lookupMode: ReportFilterLookupMode.Server,
  },
};

export function availableReportFilterDefinitions(
  features: readonly NetworkAvailableFeature[],
  allowedFields?: ReadonlySet<ReportFilterField>,
) {
  const featureSet = new Set(features);
  return Object.values(reportFilterDefinitions).filter(
    ({ field, requiredFeature }) =>
      (!allowedFields || allowedFields.has(field)) &&
      (!requiredFeature || featureSet.has(requiredFeature)),
  );
}

export const publisherReportFilterFields = new Set([
  ReportFilterField.BundleId,
  ReportFilterField.Domain,
]);

const reportFilterFieldByDimension: Partial<Record<ReportDimension, ReportFilterField>> = {
  [ReportDimension.Agency]: ReportFilterField.Agency,
  [ReportDimension.Publisher]: ReportFilterField.Publisher,
  [ReportDimension.DemandPartner]: ReportFilterField.DemandPartner,
  [ReportDimension.BidderCode]: ReportFilterField.BidderCode,
  [ReportDimension.App]: ReportFilterField.BundleId,
  [ReportDimension.Placement]: ReportFilterField.Placement,
  [ReportDimension.Country]: ReportFilterField.Country,
  [ReportDimension.DemandIntegration]: ReportFilterField.DemandIntegration,
  [ReportDimension.Platform]: ReportFilterField.Platform,
  [ReportDimension.LineItem]: ReportFilterField.LineItem,
  [ReportDimension.Application]: ReportFilterField.Application,
  [ReportDimension.PublisherDomain]: ReportFilterField.Domain,
};

/** Filter field a selected dimension can be filtered by, if it is available in this context. */
export function reportDimensionFilterField(
  dimension: ReportDimension,
  features: readonly NetworkAvailableFeature[],
  allowedFields?: ReadonlySet<ReportFilterField>,
) {
  const field = reportFilterFieldByDimension[dimension];
  if (!field) return undefined;
  return availableReportFilterDefinitions(features, allowedFields).some(
    (definition) => definition.field === field,
  )
    ? field
    : undefined;
}

export function reportFieldFilters(
  filters: readonly ReportExplorerFilter[],
  field: ReportFilterField,
) {
  return filters.filter((filter) => filter.field === field);
}

/** Replaces every filter of `field` with one filter; empty values remove the field's filter. */
export function setReportFieldFilter(
  filters: readonly ReportExplorerFilter[],
  field: ReportFilterField,
  mode: ReportFilterMode,
  values: ReportExplorerFilter['values'],
): ReportExplorerFilter[] {
  const others = filters.filter((filter) => filter.field !== field);
  return values.length ? [...others, { id: `${field}-${mode}`, field, mode, values }] : others;
}

export function reportFilterModes(field: ReportFilterField) {
  const definition = reportFilterDefinitions[field];
  return definition.excludeKey
    ? [ReportFilterMode.Include, ReportFilterMode.Exclude]
    : [ReportFilterMode.Include];
}

export function serializeReportExplorerFilters(filters: readonly ReportExplorerFilter[]) {
  const result: Partial<ReportFilter> = {};

  for (const filter of filters) {
    const serialized = serializedFilterOptions(filter);
    if (!serialized) continue;
    const { definition, key, validOptions } = serialized;
    const values = definition.numericValues
      ? validOptions.map(({ id }) => Number(id))
      : validOptions.map(({ id }) => id);
    Object.assign(result, { [key]: values });
  }

  return result;
}

export function serializeReportExplorerFilterValueLabels(filters: readonly ReportExplorerFilter[]) {
  const valueLabels: Record<string, string[]> = {};

  for (const filter of filters) {
    const serialized = serializedFilterOptions(filter);
    if (!serialized) continue;
    valueLabels[serialized.key] = serialized.validOptions.map(({ label }) => label);
  }

  return valueLabels;
}

export function deserializeReportExplorerFilters(
  filter: ReportFilter,
  valueLabels: Readonly<Record<string, readonly string[]>> = {},
) {
  const reportFilters: ReportExplorerFilter[] = [];
  const controlledKeys = new Set<string>();

  for (const definition of Object.values(reportFilterDefinitions)) {
    controlledKeys.add(definition.includeKey);
    if (definition.excludeKey) controlledKeys.add(definition.excludeKey);

    appendFilter(reportFilters, filter, valueLabels, definition, ReportFilterMode.Include);
    if (definition.excludeKey) {
      appendFilter(reportFilters, filter, valueLabels, definition, ReportFilterMode.Exclude);
    }
  }

  const passthroughFilters = Object.fromEntries(
    Object.entries(filter).filter(
      ([key]) =>
        !controlledKeys.has(key) &&
        !['dateRange', 'dateFrom', 'dateTo', 'valueLabels'].includes(key),
    ),
  ) as Partial<ReportFilter>;

  return { reportFilters, passthroughFilters };
}

function appendFilter(
  filters: ReportExplorerFilter[],
  filter: ReportFilter,
  valueLabels: Readonly<Record<string, readonly string[]>>,
  definition: AvailableReportFilterDefinition,
  mode: ReportFilterMode,
) {
  const key = mode === ReportFilterMode.Exclude ? definition.excludeKey : definition.includeKey;
  if (!key) return;
  const value = filter[key];
  if (!Array.isArray(value) || value.length === 0) return;
  const labels = valueLabels[key];
  filters.push({
    id: `${definition.field}-${mode}`,
    field: definition.field,
    mode,
    values: value.map((item, index) => ({
      id: String(item),
      label: labels?.[index] ?? String(item),
    })),
  });
}

function serializedFilterOptions(filter: ReportExplorerFilter) {
  const definition = reportFilterDefinitions[filter.field];
  const key =
    filter.mode === ReportFilterMode.Exclude ? definition.excludeKey : definition.includeKey;
  if (!key || filter.values.length === 0) return null;
  const validOptions = definition.numericValues
    ? filter.values.filter(({ id }) => Number.isFinite(Number(id)))
    : filter.values;
  return validOptions.length > 0 ? { definition, key, validOptions } : null;
}
